import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GenerationForecastDayResponse,
  PvActualDayResponse,
  fmtKwh,
  fmtInt,
  fmtDec,
} from '@/mocks/generationForecast';

function formatNumber(value: number | null | undefined, digits = 1, fallback = '\u2014'): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return fallback;
  return Number(value).toFixed(digits);
}

interface Props {
  dayData: GenerationForecastDayResponse | null;
  pvActualData: PvActualDayResponse | null;
  dayLabel: string;
  loading: boolean;
  onRefresh?: () => void;
}

type DeltaSeverity = 'zero_ok' | 'forecast_zero' | 'low' | 'high' | 'critical' | 'missing';

interface TableRow {
  hour: string;
  forecast: number | null;
  actual: number | null;
  deltaKw: number | null;
  deltaPct: number | null;
  deltaSeverity: DeltaSeverity;
  coverage: number | null;
  unit6Kw: number | null;
  shortwave: number | null;
  cloudCover: number | null;
  cloudCoverLow: number | null;
  cloudCoverMid: number | null;
  cloudCoverHigh: number | null;
  temperature: number | null;
  wind: number | null;
  precipitation: number | null;
  status: string;
  actualSamplePosition: string | null;
  actualIsFinal: boolean;
  actualStatus: string;
  readAtLocal: string | null;
  isMissing: boolean;
}

type SortKey = 'hour' | 'forecast' | 'actual' | 'deltaKw' | 'deltaPct' | 'shortwave' | 'cloudCover' | 'cloudCoverBreakdown' | 'temperature';
type SortDir = 'asc' | 'desc';

function getDeltaSeverity(forecast: number | null, actual: number | null): DeltaSeverity {
  if (forecast == null || actual == null) return 'missing';
  const f = forecast;
  const a = actual;
  if (f === 0 && a === 0) return 'zero_ok';
  if (f === 0 && a > 0) return 'forecast_zero';
  const deltaPct = ((a - f) / f) * 100;
  const absPct = Math.abs(deltaPct);
  if (absPct <= 10) return 'low';
  if (absPct <= 35) return 'high';
  return 'critical';
}

function getSeverityMeta(sev: DeltaSeverity) {
  switch (sev) {
    case 'zero_ok':
      return { labelKey: 'fc_sev_zero_ok', color: '#6B6B70', bg: 'rgba(107,107,112,0.12)' };
    case 'forecast_zero':
      return { labelKey: 'fc_sev_f0', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' };
    case 'low':
      return { labelKey: 'fc_sev_low', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' };
    case 'high':
      return { labelKey: 'fc_sev_high', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' };
    case 'critical':
      return { labelKey: 'fc_sev_critical', color: '#D12229', bg: 'rgba(209,34,41,0.12)' };
    default:
      return { labelKey: null as string | null, color: '#6B6B70', bg: 'rgba(107,107,112,0.12)' };
  }
}

export default function DeviationTable({ dayData, pvActualData, dayLabel, loading, onRefresh }: Props) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('hour');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const rows = (() => {
    const hourly = dayData?.hourly ?? [];
    const actualMap = new Map<string, PvActualDayResponse['actual'][number]>();
    if (pvActualData?.actual?.length) {
      for (const a of pvActualData.actual) {
        actualMap.set(a.hour, a);
      }
    }

    const mapped: TableRow[] = hourly.map((h) => {
      const act = actualMap.get(h.hour);
      const forecast = h.forecastEnergyKwh;
      const actual = act?.actualPacKw ?? null;
      const deltaKw = forecast != null && actual != null ? actual - forecast : null;
      const deltaPct = forecast != null && actual != null && forecast > 0 ? ((actual - forecast) / forecast) * 100 : null;
      const sev = getDeltaSeverity(forecast, actual);

      return {
        hour: h.hour,
        forecast,
        actual,
        deltaKw,
        deltaPct,
        deltaSeverity: sev,
        coverage: act?.coverage ?? null,
        unit6Kw: act?.unit6Kw ?? null,
        shortwave: h.weather?.shortwaveRadiation ?? null,
        cloudCover: h.weather?.cloudCover ?? null,
        cloudCoverLow: h.weather?.cloudCoverLow ?? null,
        cloudCoverMid: h.weather?.cloudCoverMid ?? null,
        cloudCoverHigh: h.weather?.cloudCoverHigh ?? null,
        temperature: h.weather?.temperature2m ?? null,
        wind: h.weather?.windSpeed10m ?? null,
        precipitation: h.weather?.precipitation ?? null,
        status: h.status,
        actualSamplePosition: act?.samplePosition ?? null,
        actualIsFinal: act?.isFinal ?? false,
        actualStatus: act?.status ?? 'future',
        readAtLocal: act?.readAtLocal ?? null,
        isMissing: h.status === 'missing' || h.forecastEnergyKwh == null,
      };
    });

    // Add any actual-only rows not in forecast
    const forecastHours = new Set(hourly.map((h) => h.hour));
    if (pvActualData?.actual?.length) {
      for (const a of pvActualData.actual) {
        if (!forecastHours.has(a.hour)) {
          const actual = a.actualPacKw ?? null;
          const sev = getDeltaSeverity(null, actual);
          mapped.push({
            hour: a.hour,
            forecast: null,
            actual,
            deltaKw: null,
            deltaPct: null,
            deltaSeverity: sev,
            coverage: a.coverage ?? null,
            unit6Kw: a.unit6Kw ?? null,
            shortwave: null,
            cloudCover: null,
            cloudCoverLow: null,
            cloudCoverMid: null,
            cloudCoverHigh: null,
            temperature: null,
            wind: null,
            precipitation: null,
            status: 'actual_only',
            actualSamplePosition: a.samplePosition ?? null,
            actualIsFinal: a.isFinal,
            actualStatus: a.status,
            readAtLocal: a.readAtLocal ?? null,
            isMissing: true,
          });
        }
      }
    }

    const filtered = showOnlyActive
      ? mapped.filter((r) => !r.isMissing && (r.forecast ?? 0) > 0)
      : mapped;

    return filtered.sort((a, b) => {
      const getVal = (r: TableRow, k: SortKey) => {
        if (k === 'hour') return r.hour;
        if (k === 'forecast') return r.forecast ?? -1;
        if (k === 'actual') return r.actual ?? -1;
        if (k === 'deltaKw') return r.deltaKw ?? -999999;
        if (k === 'deltaPct') return r.deltaPct ?? -999999;
        if (k === 'shortwave') return r.shortwave ?? -1;
        if (k === 'cloudCover') return r.cloudCover ?? -1;
        if (k === 'cloudCoverBreakdown') return ((r.cloudCoverLow ?? 0) + (r.cloudCoverMid ?? 0) + (r.cloudCoverHigh ?? 0)) || -1;
        return r.temperature ?? -999;
      };
      const av = getVal(a, sortKey);
      const bv = getVal(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const totalForecast = (dayData?.hourly ?? [])
    .reduce((s, h) => s + (h.forecastEnergyKwh ?? 0), 0);
  const totalActual = (pvActualData?.actual ?? [])
    .reduce((s, h) => s + (h.actualPacKw ?? 0), 0);
  const nowChips = (() => {
    const nowKey = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Kyiv' });
    const curHour = nowKey.slice(0, 2) + ':00';
    const baseNow = (dayData?.hourly ?? []).find((h) => h.hour === curHour)?.forecastEnergyKwh ?? null;
    const actualNow = (pvActualData?.actual ?? []).find((a) => a.hour === curHour)?.actualPacKw ?? null;
    const deltaKw = baseNow != null && actualNow != null ? actualNow - baseNow : null;
    const deltaPct = baseNow != null && actualNow != null && baseNow > 0 ? ((actualNow - baseNow) / baseNow) * 100 : null;
    const deltaColor = deltaKw == null ? '#9B9BA4' : deltaKw > 0 ? '#22C55E' : deltaKw < 0 ? '#D12229' : '#fff';
    return [
      { label: 'Base Forecast', value: baseNow == null ? String.fromCharCode(8212) : fmtKwh(baseNow) + ' kWh', color: '#D12229' },
      { label: 'Факт (PV)', value: actualNow == null ? String.fromCharCode(8212) : fmtKwh(actualNow) + ' kWh', color: '#22C55E' },
      { label: 'Δ', value: deltaKw == null ? String.fromCharCode(8212) : (deltaKw > 0 ? '+' : '') + fmtKwh(deltaKw) + ' кВт·г' + (deltaPct != null ? ' / ' + (deltaPct > 0 ? '+' : '') + fmtDec(deltaPct, 1) + '%' : ''), color: deltaColor },
    ];
  })();


  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <i className={`ri-arrow-up-s-line text-[10px] leading-none ${sortKey === col && sortDir === 'asc' ? 'opacity-100' : ''}`}></i>
      <i className={`ri-arrow-down-s-line text-[10px] leading-none -mt-1 ${sortKey === col && sortDir === 'desc' ? 'opacity-100' : ''}`}></i>
    </span>
  );

  if (loading) {
    return (
      <div
        className="rounded-xl p-8 flex items-center justify-center"
        style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.07)', minHeight: 240 }}
      >
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
          <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>{t('fc_loading_hourly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl"
      style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between px-4 md:px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-white font-semibold text-base" style={{ fontFamily: 'Inter, sans-serif' }}>{t('fc_dev_title')}</div>
          <div className="flex items-center gap-2 flex-wrap">
            {nowChips.map((c) => (
              <div
                key={c.label}
                className="flex items-center justify-center gap-1.5 rounded-md text-xs font-medium whitespace-nowrap"
                style={{ minWidth: 140, padding: '6px 14px', background: c.color + '1F', border: '1px solid ' + c.color + '55' }}
              >
                <div className="w-2 h-2 rounded-full" style={{ background: c.color }}></div>
                <span style={{ color: '#9B9BA4' }}>{c.label}</span>
                <span className="font-semibold" style={{ color: c.color }}>{c.value}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowOnlyActive((v) => !v)}
          className="text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap"
          style={{
            background: showOnlyActive ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)',
            color: showOnlyActive ? '#22C55E' : '#A0A0A8',
            border: '1px solid ' + (showOnlyActive ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'),
          }}
        >
          {showOnlyActive ? t('fc_active_only') : t('fc_all_hours')}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs md:text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {([
                ['hour', t('fc_col_hour')],
                ['forecast', t('fc_col_forecast_kwh')],
                ['actual', t('fc_col_actual_88')],
                ['deltaKw', 'Δ, kWh'],
                ['deltaPct', 'Δ, %'],
                ['shortwave', t('fc_col_shortwave')],
                ['cloudCover', t('fc_col_cloud')],
                ['cloudCoverBreakdown', t('fc_col_cloud_lmh')],
                ['temperature', t('fc_col_temp')],
              ] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="text-left px-2 md:px-3 py-2 md:py-3 font-medium cursor-pointer select-none whitespace-nowrap"
                  style={{ color: sortKey === key ? '#D12229' : '#6B6B70', fontFamily: 'Inter, sans-serif', fontSize: 10 }}
                >
                  {label}<SortIcon col={key} />
                </th>
              ))}
              <th className="text-left px-2 md:px-3 py-2 md:py-3 font-medium whitespace-nowrap" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif', fontSize: 10 }}>{t('fc_col_sev')}</th>
              <th className="text-left px-2 md:px-3 py-2 md:py-3 font-medium whitespace-nowrap" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif', fontSize: 10 }}>{t('fc_col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h, idx) => {
              const mainColor = h.isMissing ? '#4A4A50' : '#fff';
              const secondaryColor = h.isMissing ? '#4A4A50' : '#A0A0A8';
              const sev = getSeverityMeta(h.deltaSeverity);
              const deltaColor =
                h.deltaKw == null
                  ? '#4A4A50'
                  : h.deltaKw > 0
                    ? '#22C55E'
                    : h.deltaKw < 0
                      ? '#D12229'
                      : '#fff';
              const deltaPctColor =
                h.deltaPct == null
                  ? '#4A4A50'
                  : h.deltaPct > 0
                    ? '#22C55E'
                    : h.deltaPct < 0
                      ? '#D12229'
                      : '#fff';

              return (
                <tr
                  key={h.hour}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                  }}
                >
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm font-medium" style={{ color: mainColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.hour}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: h.forecast == null ? '#4A4A50' : '#D12229', fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.forecast == null ? '—' : fmtKwh(h.forecast)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: h.actual == null ? '#4A4A50' : '#22C55E', fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.actual == null ? '—' : fmtKwh(h.actual)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: deltaColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.deltaKw == null ? '—' : `${h.deltaKw > 0 ? '+' : ''}${fmtKwh(h.deltaKw)}`}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: deltaPctColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.deltaPct == null ? '—' : `${h.deltaPct > 0 ? '+' : ''}${fmtDec(h.deltaPct, 1)}%`}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.shortwave == null ? '—' : fmtInt(h.shortwave)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.cloudCover == null ? '—' : fmtDec(h.cloudCover, 1)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                    <span style={{ color: '#FCA5A5' }}>{h.cloudCoverLow == null ? '–' : Math.round(h.cloudCoverLow)}</span>
                    {' / '}
                    <span style={{ color: '#FCD34D' }}>{h.cloudCoverMid == null ? '–' : Math.round(h.cloudCoverMid)}</span>
                    {' / '}
                    <span style={{ color: '#93C5FD' }}>{h.cloudCoverHigh == null ? '–' : Math.round(h.cloudCoverHigh)}</span>
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.temperature == null ? '—' : fmtDec(h.temperature, 1)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3">
                    <span
                      className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 md:py-1 rounded-full whitespace-nowrap"
                      style={{ color: sev.color, background: sev.bg }}
                    >
                      {sev.labelKey ? t(sev.labelKey) : '—'}
                    </span>
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3">
                    {h.actualStatus === 'future' ? (
                      <span
                        className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 md:py-1 rounded-full whitespace-nowrap"
                        style={{ color: '#9B9BA4', background: 'rgba(155,155,164,0.12)' }}
                      >{t('fc_st_future')}</span>
                    ) : h.actualSamplePosition === 'live_current' ? (
                      <span
                        className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 md:py-1 rounded-full whitespace-nowrap"
                        style={{ color: '#F59E0B', background: 'rgba(245,158,11,0.12)' }}
                      >{t('fc_st_live')}</span>
                    ) : h.actualSamplePosition === 'H+30' && h.actualIsFinal ? (
                      <span
                        className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 md:py-1 rounded-full whitespace-nowrap"
                        style={{ color: '#22C55E', background: 'rgba(34,197,94,0.12)' }}
                      >{t('fc_st_final')}</span>
                    ) : (
                      <span
                        className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 md:py-1 rounded-full whitespace-nowrap"
                        style={{ color: '#9B9BA4', background: 'rgba(155,155,164,0.12)' }}
                      >{t('fc_st_no_data')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}